import axiosInstance from "./axiosInstance";

// Screening runs before interview scoring exists, so it can't rely on
// TotalScoreManagement's interview-scored candidate list (fetchInterviewData) —
// that inner-joins TotalScore/InterviewScore and 404s until someone's been
// interviewed. CandidateManagement has no such requirement.
export const getCandidatesForJobAPI = async (jobId) => {
  try {
    const response = await axiosInstance.get(`candidate/${jobId}/`);
    const candidates = response.data["Candidate Data"] || [];
    // The endpoint now emits the canonical "Resume Id" alongside the legacy "Resume ID".
    // The fallback stays only until the backend deletes the legacy spelling.
    return candidates.map((c) => ({
      "Resume Id": c["Resume Id"] ?? c["Resume ID"],
      "Candidate name": c["Candidate Name"],
      "Candidate Phone": c["Candidate Phone"],
    }));
  } catch (error) {
    if (error.response?.status === 404) {
      return [];
    }
    throw new Error(error.response?.data?.message || "Failed to fetch candidates");
  }
};

export const scheduleScreeningCallAPI = async (jobId, resumeId, additionalInfo = {}) => {
  try {
    const response = await axiosInstance.post("screening/schedule", {
      job_id: jobId,
      resume_id: resumeId,
      additional_info: additionalInfo,
    });
    return response.data;
  } catch (error) {
    const msg = error.response?.data?.message || "Failed to schedule screening call";
    throw new Error(msg);
  }
};

// Multiple screening attempts can exist for the same candidate/phone number —
// always surface the highest-scoring completed attempt, not just the most recent one.
export const getBestScreeningResult = (results = []) => {
  return results.reduce((best, r) => {
    if (r.interview_rating == null) return best;
    if (!best || r.interview_rating > best.interview_rating) return r;
    return best;
  }, null);
};

/**
 * The screening_data that a candidate's table row should show.
 *
 * The table is one row per candidate but screening_data is per attempt, so something has to
 * choose. Prefer the same attempt the Screen-score column keys off — otherwise the two columns
 * would silently describe different calls.
 *
 * But getBestScreeningResult ranks by interview_rating and returns null when nothing was rated,
 * and an answered call can carry a complete screening_data block with no rating at all (a
 * followup_no_time outcome, say). So fall back to the most recent attempt that actually has
 * data, rather than rendering "—" over data we are holding. results[0] is the latest — the API
 * orders by created_at desc.
 */
export const getScreeningData = (results = []) => {
  const hasData = (r) => Boolean(r?.screening_data) && Object.keys(r.screening_data).length > 0;

  const best = getBestScreeningResult(results);
  if (hasData(best)) return best.screening_data;

  return (results || []).find(hasData)?.screening_data || {};
};

/**
 * Every candidate's screening status for a job, in ONE request.
 *
 * Replaces fanning out a request per candidate and then polling all of them. Each entry
 * has the same body as the per-candidate endpoint minus the echoed ids, so a single parser
 * handles both.
 *
 * Candidates with no screening call are ABSENT from the map — missing means "not
 * scheduled", not "failed to load".
 */
export const getScreeningStatusesAPI = async (jobId) => {
  const response = await axiosInstance.get(`screening/status/${jobId}`);
  const data = response.data;
  if (!data) return {};

  // Expected: a bare object keyed by resume_id. Be tolerant of the two other shapes an API
  // like this tends to return, rather than silently yielding an empty map — an empty map is
  // indistinguishable from "nobody has been screened", which is how a shape mismatch here
  // hides as a UI that just says "no screening call scheduled".
  if (Array.isArray(data)) {
    return Object.fromEntries(
      data.map((entry) => [entry.resume_id ?? entry["Resume Id"], entry])
    );
  }

  const wrapped = data.statuses || data.results || data.data;
  if (wrapped && typeof wrapped === "object") return wrapped;

  return data;
};

/**
 * Tells the lead scheduler to stop (pause=true) or carry on (pause=false) calling this candidate.
 *
 * The server refuses a call that has already finished, and only records the pause once the vendor
 * has confirmed it — so a rejection here means the candidate is still being called. Surface the
 * server's message rather than a generic one: "already finished" and "couldn't reach the
 * scheduler" need different reactions from the recruiter.
 */
export const pauseScreeningCallAPI = async (jobId, resumeId, pause) => {
  try {
    const response = await axiosInstance.post("screening/pause", {
      job_id: jobId,
      resume_id: resumeId,
      pause,
    });
    return response.data;
  } catch (error) {
    const fallback = pause ? "Failed to pause screening call" : "Failed to resume screening call";
    throw new Error(error.response?.data?.message || fallback);
  }
};

export const getScreeningStatusAPI = async (jobId, resumeId) => {
  try {
    const response = await axiosInstance.get(`screening/status/${jobId}/${resumeId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return { job_id: jobId, resume_id: resumeId, status: null, results: [] };
    }
    throw new Error(error.response?.data?.message || "Failed to fetch screening status");
  }
};
